// courseService.js
import { apiClient, API_ENDPOINTS } from './api';
import { cacheService } from './cacheService';

export const courseService = {
  // Флаг для отслеживания загрузки категорий
  _isLoadingCategories: false,
  _categoriesPromise: null,

  // Получить все курсы с автоматической пагинацией
  async getAll(pageSize = 100) {
  try {
    const response = await apiClient.get(API_ENDPOINTS.COURSES);
    
    // Нормализация ответа сервера
    let items = [];
    let meta = null;
    
    // Сервер возвращает: { "courses": [...] }
    if (response && response.courses && Array.isArray(response.courses)) {
      items = response.courses;
    }
    // Если ответ - массив курсов
    else if (Array.isArray(response)) {
      items = response;
    }
    // Если ответ с пагинацией
    else if (response?.data && Array.isArray(response.data)) {
      items = response.data;
      meta = response.meta;
    }
    // Если ответ с полем data и внутри courses
    else if (response?.data?.courses && Array.isArray(response.data.courses)) {
      items = response.data.courses;
      meta = response.data.meta;
    }
    
    // ВАЖНО: Явно убеждаемся, что поле is_show сохраняется
    // Если в ответе нет is_show, но есть isShow или show_in_card
    items = items.map(item => ({
      ...item,
      is_show: item.is_show !== undefined ? item.is_show : (item.isShow || item.show_in_card || false)
    }));
    
    // Если есть пагинация и нужно загрузить все страницы
    if (meta && meta.total > items.length) {
      return await this.getAllWithPagination(items, meta, pageSize);
    }
    
    return items;
  } catch (error) {
    throw error;
  }
},

  // Вспомогательный метод для загрузки всех страниц
  async getAllWithPagination(currentItems, meta, pageSize = 100) {
  const items = [...currentItems];
  const total = meta.total;
  const perPage = meta.per_page || pageSize;
  const totalPages = Math.ceil(total / perPage);
  const currentPage = meta.current_page || 1;
  
  if (totalPages <= currentPage) {
    return items;
  }
  
  // Параллельная загрузка остальных страниц
  const remainingPages = [];
  for (let page = currentPage + 1; page <= totalPages; page++) {
    remainingPages.push(
      apiClient.get(`${API_ENDPOINTS.COURSES}?page=${page}&per_page=${perPage}`)
    );
  }
  
  const remainingResponses = await Promise.all(remainingPages);
  
  for (const response of remainingResponses) {
    let pageItems = [];
    
    if (Array.isArray(response)) {
      pageItems = response;
    } else if (response?.data && Array.isArray(response.data)) {
      pageItems = response.data;
    } else if (response?.courses && Array.isArray(response.courses)) {
      pageItems = response.courses;
    } else if (response?.data?.courses && Array.isArray(response.data.courses)) {
      pageItems = response.data.courses;
    }
    
    if (Array.isArray(pageItems)) {
      // Нормализуем is_show для каждой страницы
      pageItems = pageItems.map(item => ({
        ...item,
        is_show: item.is_show !== undefined ? item.is_show : (item.isShow || item.show_in_card || false)
      }));
      items.push(...pageItems);
    }
  }
  
  return items;
},

  async getById(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE(id));
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async create(courseData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSES, courseData);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async update(id, courseData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.COURSE(id), courseData);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async delete(id) {
    try {
      await apiClient.delete(API_ENDPOINTS.COURSE(id));
      return true;
    } catch (error) {
      throw error;
    }
  },

  // Обновить статус отображения курса в карточке (is_show)
  // Используем PUT метод, так как PATCH не поддерживается
  async updateShowStatus(id, isShow) {
    try {
      // Получаем текущие данные курса
      const currentCourse = await this.getById(id);
      
      // Отправляем PUT запрос со всеми данными + обновленным is_show
      const response = await apiClient.put(API_ENDPOINTS.COURSE(id), {
        name: currentCourse.name,
        type: currentCourse.type || null,
        direction: currentCourse.direction || null,
        category_id: currentCourse.category_id || null,
        subcategory: currentCourse.subcategory || null,
        legal_basis: currentCourse.legal_basis || null,
        duration_hours: currentCourse.duration_hours || null,
        periodicity_months: currentCourse.periodicity_months || null,
        is_show: isShow
      });
      return response.data || response;
    } catch (error) {
      console.error(`Ошибка обновления is_show для курса ${id}:`, error);
      throw error;
    }
  },

  // Защита от дублирования запросов категорий
  async getCategories(forceRefresh = false) {
    // Если уже идет загрузка и не принудительное обновление, возвращаем существующий Promise
    if (this._isLoadingCategories && !forceRefresh) {
      return this._categoriesPromise;
    }
    
    try {
      // Добавляем кэширование
      if (!forceRefresh) {
        const cached = cacheService.get('course_categories');
        if (cached) return cached;
      }
      
      this._isLoadingCategories = true;
      this._categoriesPromise = this._fetchCategories();
      
      const categories = await this._categoriesPromise;
      return categories;
    } finally {
      this._isLoadingCategories = false;
      this._categoriesPromise = null;
    }
  },
  
  async _fetchCategories() {
    const response = await apiClient.get(API_ENDPOINTS.COURSES_CATEGORIES);
    
    // Нормализация данных - всегда берем categories_with_details
    let categories = [];
    
    // Приоритет: categories_with_details (содержит id и name)
    if (response?.categories_with_details && Array.isArray(response.categories_with_details)) {
      categories = response.categories_with_details;
    } 
    // Fallback: categories (массив строк) - преобразуем в объекты
    else if (response?.categories && Array.isArray(response.categories)) {
      categories = response.categories.map((name, index) => ({
        id: index + 1,
        name: name,
        sort_order: index + 1,
        courses_count: 0
      }));
    }
    // Другие возможные форматы
    else if (Array.isArray(response)) {
      if (response.length > 0 && typeof response[0] === 'object' && response[0].id) {
        categories = response;
      } else if (response.length > 0 && typeof response[0] === 'string') {
        categories = response.map((name, index) => ({
          id: index + 1,
          name: name,
          courses_count: 0
        }));
      }
    }
    else if (response?.data && Array.isArray(response.data)) {
      categories = response.data;
    }
    
    // Сохраняем в кэш
    cacheService.set('course_categories', categories);
    
    return categories;
  },

  async getRequiredByPosition(positionId) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSES_REQUIRED(positionId));
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async search(query) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.COURSES}?search=${encodeURIComponent(query)}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async getByCategory(categoryId) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.COURSES}?category_id=${categoryId}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async getByType(type) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.COURSES}?type=${encodeURIComponent(type)}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async assignToEmployee(employeeId, courseId, assignedDate, expirationDate) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.TRAININGS_ASSIGN, {
        employee_id: employeeId,
        course_id: courseId,
        assigned_date: assignedDate,
        expiration_date: expirationDate
      });
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },

  async assignToBrigade(brigadeId, courseId, assignedDate, expirationDate) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.TRAININGS_BULK_ASSIGN, {
        brigade_id: brigadeId,
        course_id: courseId,
        assigned_date: assignedDate,
        expiration_date: expirationDate
      });
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }
};